import { useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import * as echarts from 'echarts'
import { ChartTimeContext, type ChartTimeNavigation } from './ChartTimeNavigation'
import { SCREENER_CHART_GROUP } from './chartUtils'

/** Keep visited charts mounted, but apply series and linked navigation only
 * near the viewport. On re-entry both are applied together before the next paint.
 */
export function DeferredChartPane({ children, height }: { children: ReactNode; height: number }) {
  const container = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(typeof IntersectionObserver === 'undefined')
  const navigation = useContext(ChartTimeContext)
  const retained = useRef<{ children: ReactNode; navigation: ChartTimeNavigation | null } | null>(null)
  if (active) retained.current = { children, navigation }
  const content = retained.current
  const mounted = content !== null

  useEffect(() => {
    if (!container.current || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(entries => setActive(entries.some(entry => entry.isIntersecting)), {
      rootMargin: '320px 0px',
    })
    observer.observe(container.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    // ECharts' connected groups would otherwise run dataZoom work in every
    // hidden canvas even while React's data/options are intentionally retained.
    const synchronizeGroup = () => container.current?.querySelectorAll<HTMLElement>('[_echarts_instance_]').forEach(node => {
      const chart = echarts.getInstanceByDom(node)
      if (chart) chart.group = active ? SCREENER_CHART_GROUP : ''
    })
    synchronizeGroup()
    const frame = requestAnimationFrame(synchronizeGroup)
    return () => cancelAnimationFrame(frame)
  }, [active, mounted])

  return <div ref={container} data-chart-render-active={active} style={content ? undefined : { minHeight: height }}>
    {content ? <ChartTimeContext.Provider value={content.navigation}>{content.children}</ChartTimeContext.Provider>
      : <div className="flex items-center justify-center text-xs text-slate-500" style={{ height }} role="status">Loading chart…</div>}
  </div>
}
